import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import { ubikaLead } from "../api/dashboard"; // 👈 Import ya listo

function Hero3Contact() {
  const [contact, setContact] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });

  const parseUtm = () => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (let [key, value] of params.entries()) {
      if (key.startsWith("utm_")) utm[key] = value;
    }
    return utm;
  };

  const validate = () => {
    if (!contact.trim()) {
      setError("Ingresá tu WhatsApp o correo electrónico.");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const body = {
        contact: contact.trim(),
        source: "hero3", // origen del lead
        page: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ts: new Date().toISOString(),
        utm: parseUtm(),
      };

      const res = await ubikaLead(body);
      console.log(res);

      if (res?.data?.success || res?.status === 200) {
        setSnack({
          open: true,
          type: "success",
          msg: "¡Listo! Te contactamos a la brevedad.",
        });
        setContact("");
      } else {
        throw new Error(res?.data?.message || "Error desconocido");
      }
    } catch (error) {
      console.error("Error enviando lead:", error);
      setSnack({
        open: true,
        type: "error",
        msg: "No pudimos enviar tu solicitud. Probá de nuevo en un momento.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        scrollSnapAlign: "start",
        background: "linear-gradient(180deg, #903AF2, #d2c2f2)",
        py: { xs: 6, md: 10 },
        color: "white",
        height: "100%",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Formulario */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={6}
              sx={{
                p: 4,
                borderRadius: 3,
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ color: "#903AF2", textAlign: "center" }}
              >
                Hablemos de tu proyecto
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#555", mb: 3, textAlign: "center" }}
              >
                Dejanos tu WhatsApp o correo y te responderemos en menos de 24h.
              </Typography>

              <TextField
                fullWidth
                type="tel"
                placeholder="Ej: +54 9 360 000 0000 o email@email.com"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                error={!!error}
                helperText={error}
                onFocus={() => setContact("")}
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={loading}
                endIcon={!loading && <SendIcon />}
                sx={{
                  backgroundColor: "#903AF2",
                  color: "#fff",
                  fontWeight: "bold",
                  "&:hover": { backgroundColor: "#7029b3" },
                  borderRadius: 2,
                  py: 1.5,
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Enviar"
                )}
              </Button>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  textAlign: "center",
                  color: "#777",
                }}
              >
                📌 Tus datos están seguros, no los compartimos con terceros.
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 3, justifyContent: "center" }}
              >
                <IconButton href="tel:+5492604206967" sx={{ color: "#903AF2" }}>
                  <PhoneIcon />
                </IconButton>
                <IconButton
                  href="https://wa.me/5492604206967"
                  sx={{ color: "#903AF2" }}
                >
                  <WhatsAppIcon />
                </IconButton>
                <IconButton
                  href="https://www.instagram.com/ubikasite/"
                  target="_blank"
                  sx={{ color: "#903AF2" }}
                >
                  <InstagramIcon />
                </IconButton>
              </Stack>
            </Paper>
          </Grid>

          {/* Mapa e info */}
          <Grid item xs={12} md={6} sx={{ textAlign: "center" }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Nuestra Ubicación
            </Typography>

            <Box
              component="iframe"
              src="https://www.google.com/maps?q=Av+Mitre+1362,+San+Rafael,+Mendoza,+Argentina&output=embed"
              width="100%"
              height="300"
              style={{
                border: 0,
                borderRadius: "12px",
                maxWidth: "500px",
              }}
              loading="lazy"
            />

            <Typography
              variant="body2"
              sx={{ color: "#fff", mt: 2, maxWidth: "500px", mx: "auto" }}
            >
              ✨ En Ubika creemos en la energía de las ideas y la magia de
              hacerlas realidad. Estamos listos para ayudarte a dar el próximo
              paso.
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.type}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Hero3Contact;
